import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { threadPinActionEvent } from '#pplx/frontend/apps/ask/events/thread/analytics.js';
import { useRefetchBookmarks } from '#pplx/frontend/apps/ask/hooks/collections-hooks/useBookmarksQuery.js';
import { markComputerDashboardGraphQLCacheStale } from '#pplx/frontend/apps/ask/hooks/computer-hooks/computerDashboardGraphQLCacheState.js';
import { runChunkedBulkAction, settleBulkAction, } from '#pplx/frontend/apps/ask/queries/mutations/bulkThreadActions.js';
import { batchPinThreadsMutation, batchUnpinThreadsMutation, } from '#pplx/frontend/apps/ask/queries/mutations/threadMutations.js';
import { getSidebarV2InitialQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { getPinnedItemsQueryKey, getPinnedThreadsQueryKey, getThreadsQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { getValidPinnedThreadContextUuid } from '#pplx/frontend/apps/ask/thread-list/pinnedThreadItem.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
const PIN_REASON = 'pin-thread';
const UNPIN_REASON = 'unpin-thread';
function getValidThreads(threads) {
    const seen = new Set();
    return threads.filter((thread) => {
        if (!thread.contextUUID || seen.has(thread.contextUUID))
            return false;
        seen.add(thread.contextUUID);
        return true;
    });
}
function optimisticallyUnpinFromPinnedQueries(queryClient, contextUuids) {
    const contextUuidSet = new Set(contextUuids);
    const pinnedItems = queryClient.getQueriesData({
        queryKey: getPinnedItemsQueryKey(),
    });
    const pinnedThreads = queryClient.getQueriesData({
        queryKey: getPinnedThreadsQueryKey(),
    });
    const recentThreads = queryClient.getQueriesData({
        queryKey: getThreadsQueryKey(),
    });
    queryClient.setQueriesData({ queryKey: getPinnedItemsQueryKey() }, (items) => items?.filter((item) => !contextUuidSet.has(getValidPinnedThreadContextUuid(item) ?? '')));
    queryClient.setQueriesData({ queryKey: getPinnedThreadsQueryKey() }, (threads) => threads?.filter((thread) => !contextUuidSet.has(thread.context_uuid)));
    queryClient.setQueriesData({ queryKey: getThreadsQueryKey() }, (data) => data && {
        ...data,
        pages: data.pages.map((page) => page.map((thread) => contextUuidSet.has(thread.context_uuid) ? { ...thread, is_pinned: false } : thread)),
    });
    return { pinnedItems, pinnedThreads, recentThreads };
}
function restoreFailedPinnedQueryUpdates(queryClient, snapshot, succeededContextUuids) {
    const succeededContextUuidSet = new Set(succeededContextUuids);
    for (const [queryKey, items] of snapshot.pinnedItems) {
        queryClient.setQueryData(queryKey, items?.filter((item) => !succeededContextUuidSet.has(getValidPinnedThreadContextUuid(item) ?? '')));
    }
    for (const [queryKey, threads] of snapshot.pinnedThreads) {
        queryClient.setQueryData(queryKey, threads?.filter((thread) => !succeededContextUuidSet.has(thread.context_uuid)));
    }
    for (const [queryKey, data] of snapshot.recentThreads) {
        queryClient.setQueryData(queryKey, data && {
            ...data,
            pages: data.pages.map((page) => page.map((thread) => succeededContextUuidSet.has(thread.context_uuid)
                ? { ...thread, is_pinned: false }
                : thread)),
        });
    }
}
export function useThreadPinActions({ onSuccess, onOptimisticPin, onOptimisticUnpin, surface, threadType, }) {
    const { track } = useInstrumentation();
    const refetchBookmarks = useRefetchBookmarks();
    const queryClient = useQueryClient();
    const { openToast } = useToastContext();
    const { $t } = useIntl();
    // One batch request per chunk instead of one request per thread.
    const { mutateAsync: pinTasks, isPending: isPinning } = useMutation({
        mutationFn: (contextUuids) => runChunkedBulkAction({
            contextUuids,
            execute: (chunk) => batchPinThreadsMutation({ contextUUIDs: chunk, reason: PIN_REASON }),
        }),
    });
    const { mutateAsync: unpinTasks, isPending: isUnpinning } = useMutation({
        mutationFn: (contextUuids) => runChunkedBulkAction({
            contextUuids,
            execute: (chunk) => batchUnpinThreadsMutation({ contextUUIDs: chunk, reason: UNPIN_REASON }),
        }),
    });
    const runPinAction = useCallback(async ({ threads, options, mutateAsync, onOptimisticUpdate, updatePinnedCaches = false, eventName, threadType, failureMessage, partialFailureMessage, }) => {
        const validThreads = getValidThreads(threads);
        if (validThreads.length === 0)
            return;
        if (updatePinnedCaches) {
            await Promise.all([
                queryClient.cancelQueries({ queryKey: getPinnedItemsQueryKey() }),
                queryClient.cancelQueries({ queryKey: getPinnedThreadsQueryKey() }),
                queryClient.cancelQueries({ queryKey: getThreadsQueryKey() }),
            ]);
        }
        const pinnedQuerySnapshot = updatePinnedCaches
            ? optimisticallyUnpinFromPinnedQueries(queryClient, validThreads.map((thread) => thread.contextUUID))
            : undefined;
        const optimisticUpdates = new Map();
        for (const thread of validThreads) {
            const update = onOptimisticUpdate?.(thread);
            if (update)
                optimisticUpdates.set(thread.contextUUID, update);
        }
        const contextUuids = validThreads.map((thread) => thread.contextUUID);
        const outcome = await settleBulkAction(contextUuids, () => mutateAsync(contextUuids));
        const failedContextUuids = new Set(outcome.failedContextUuids);
        if (pinnedQuerySnapshot && failedContextUuids.size > 0) {
            restoreFailedPinnedQueryUpdates(queryClient, pinnedQuerySnapshot, outcome.succeededContextUuids);
        }
        for (const thread of validThreads) {
            const update = optimisticUpdates.get(thread.contextUUID);
            if (failedContextUuids.has(thread.contextUUID)) {
                update?.rollback();
                continue;
            }
            update?.commit();
            // Without a dashboard-store update, the GraphQL preload cache must
            // refetch to reflect this mutation on the next dashboard visit.
            if (!update && threadType === 'computer') {
                markComputerDashboardGraphQLCacheStale();
            }
        }
        for (const contextUuid of outcome.succeededContextUuids) {
            track(threadPinActionEvent({
                name: eventName,
                data: {
                    session_id: contextUuid,
                    surface,
                },
            }));
        }
        if (outcome.succeededContextUuids.length > 0) {
            refetchBookmarks();
            queryClient.invalidateQueries({ queryKey: getPinnedItemsQueryKey(), exact: false });
            queryClient.invalidateQueries({ queryKey: getPinnedThreadsQueryKey(), exact: false });
            // user pins do not change the independent project-pin ordering.
            void queryClient.invalidateQueries({ queryKey: getSidebarV2InitialQueryKey() });
            queryClient.invalidateQueries({ queryKey: getThreadsQueryKey() });
            onSuccess?.();
        }
        if (failedContextUuids.size === 0)
            return;
        openToast({
            message: outcome.succeededContextUuids.length > 0
                ? partialFailureMessage(outcome.succeededContextUuids.length, validThreads.length)
                : failureMessage(validThreads.length),
            variant: outcome.succeededContextUuids.length > 0 ? 'warning' : 'error',
            timeoutSeconds: 7,
        });
        options?.onFailure?.(outcome.failedContextUuids);
    }, [onSuccess, openToast, queryClient, refetchBookmarks, surface, track]);
    const pinThreads = useCallback((threads, options) => {
        void runPinAction({
            threads,
            options,
            mutateAsync: pinTasks,
            onOptimisticUpdate: onOptimisticPin,
            eventName: threadType === 'computer' ? 'computer task pinned' : 'search thread pinned',
            threadType,
            failureMessage: (totalCount) => threadType === 'computer'
                ? $t({
                    defaultMessage: 'Unable to pin {totalCount, plural, one {session} other {sessions}}.',
                    id: '9f83f40360',
                    description: 'Error toast shown when pinning computer tasks fails',
                }, { totalCount })
                : $t({
                    defaultMessage: 'Unable to pin this session.',
                    id: '297be495d7',
                    description: 'Error toast shown when pinning a search thread fails',
                }),
            partialFailureMessage: (successCount, totalCount) => $t({
                defaultMessage: 'Pinned {successCount} of {totalCount} sessions.',
                id: '27f79c74c3',
                description: 'Partial failure toast shown when pinning multiple computer tasks',
            }, { successCount, totalCount }),
        });
    }, [$t, onOptimisticPin, pinTasks, runPinAction, threadType]);
    const unpinThreads = useCallback((threads, options) => {
        void runPinAction({
            threads,
            options,
            mutateAsync: unpinTasks,
            onOptimisticUpdate: onOptimisticUnpin,
            updatePinnedCaches: true,
            eventName: threadType === 'computer' ? 'computer task unpinned' : 'search thread unpinned',
            threadType,
            failureMessage: (totalCount) => threadType === 'computer'
                ? $t({
                    defaultMessage: 'Unable to unpin {totalCount, plural, one {session} other {sessions}}.',
                    id: '1eaa4971ad',
                    description: 'Error toast shown when unpinning computer tasks fails',
                }, { totalCount })
                : $t({
                    defaultMessage: 'Unable to unpin this session.',
                    id: '13c42c64bb',
                    description: 'Error toast shown when unpinning a search thread fails',
                }),
            partialFailureMessage: (successCount, totalCount) => $t({
                defaultMessage: 'Unpinned {successCount} of {totalCount} sessions.',
                id: 'a635dd7360',
                description: 'Partial failure toast shown when unpinning multiple computer tasks',
            }, { successCount, totalCount }),
        });
    }, [$t, onOptimisticUnpin, runPinAction, threadType, unpinTasks]);
    return {
        pinThreads,
        unpinThreads,
        isMutatingPinState: isPinning || isUnpinning,
    };
}
export function useComputerTaskPinActions(args) {
    return useThreadPinActions({ ...args, threadType: 'computer' });
}
export function useThreadPinAction({ contextUuid, isPinned, onSuccess, onToggle, onOptimisticPin, onOptimisticUnpin, surface, threadType, }) {
    const { pinThreads, unpinThreads, isMutatingPinState } = useThreadPinActions({
        onSuccess,
        onOptimisticPin,
        onOptimisticUnpin,
        surface,
        threadType,
    });
    const handlePinToggle = useCallback(() => {
        if (!contextUuid)
            return;
        onToggle?.();
        if (isPinned) {
            unpinThreads([{ contextUUID: contextUuid }]);
            return;
        }
        pinThreads([{ contextUUID: contextUuid }]);
    }, [contextUuid, isPinned, onToggle, pinThreads, unpinThreads]);
    return {
        handlePinToggle,
        isMutatingPinState,
    };
}
export function useComputerTaskPinAction(args) {
    return useThreadPinAction({ ...args, threadType: 'computer' });
}
//# sourceMappingURL=useThreadPinAction.js.map